/**
 * Hook personnalisé pour gérer les horaires de service
 *
 * Ce hook permet de récupérer les horaires d'ouverture d'un service spécifique
 * pour une date donnée, en tenant compte de la configuration de l'établissement
 * et des exceptions éventuelles.
 *
 * @module useServiceHours
 */

import {useMemo} from 'react';
import {format} from 'date-fns';
import {fr} from 'date-fns/locale';
import {findExceptionForDay} from '../utils/restaurant/exceptionDayResolver';
import {serviceArrivalWindow, MINUTES_PER_DAY} from '../utils/restaurant/serviceArrivalWindow';

/**
 * Récupère les exceptions applicables à une date donnée
 *
 * Gère les 3 types d'exceptions :
 * - 'unique' : Exception pour une date spécifique (ex: 25/12/2024)
 * - 'range' : Exception pour une plage de dates (ex: vacances 01/07 - 31/08)
 * - 'recurring' : Exception récurrente chaque semaine (ex: tous les lundis)
 *
 * Délègue à la source unique `exceptionDayResolver` : au plus une exception est
 * renvoyée, selon la priorité date unique > plage > récurrence.
 *
 * @param {Array<Object>} exceptions - Liste des exceptions de la configuration
 * @param {Date} date - Date à vérifier
 * @return {Array<Object>} Liste des exceptions applicables à cette date
 *
 * @example
 * const exceptions = [
 *   { type: 'unique', date: '2024-12-25', isClosed: true, name: 'Noël' },
 *   { type: 'range', range: { start: '2024-07-01', end: '2024-08-31' }, services: [...] },
 *   { type: 'recurring', dayOfWeek: 'monday', isClosed: true, name: 'Fermé le lundi' }
 * ];
 * const applicable = getApplicableExceptions(exceptions, new Date('2024-12-25'));
 * // Returns: [{ type: 'unique', date: '2024-12-25', isClosed: true, name: 'Noël' }]
 */
export const getApplicableExceptions = (exceptions, date) => {
  const exception = findExceptionForDay(exceptions, date);
  return exception ? [exception] : [];
};

/**
 * Correspondance jour français (date-fns, sans accents) vers clé anglaise du
 * `serviceSchedule`. Source unique pour tout le hook.
 */
const DAY_MAPPING_FR_EN = {
  'lundi': 'monday',
  'mardi': 'tuesday',
  'mercredi': 'wednesday',
  'jeudi': 'thursday',
  'vendredi': 'friday',
  'samedi': 'saturday',
  'dimanche': 'sunday',
};

/**
 * Clé anglaise du jour de semaine (`monday`…`sunday`) pour une date.
 *
 * @param {Date} date - Date à convertir
 * @return {string|null} Clé anglaise du jour, ou null si non résolu
 */
const englishWeekdayFor = (date) => {
  const dayOfWeek = format(date, 'EEEE', {locale: fr})
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  return DAY_MAPPING_FR_EN[dayOfWeek] || null;
};

/**
 * Services ACTIFS du planning hebdomadaire normal pour une date (hors exception).
 *
 * @param {Object} restaurantConfig - Configuration (`serviceSchedule`)
 * @param {Date} date - Date visée
 * @return {Array<Object>} Services normaux `{start, end, name, active, isException:false}`
 */
export const getNormalServicesForDate = (restaurantConfig, date) => {
  if (!restaurantConfig || !date) return [];
  const englishDay = englishWeekdayFor(date);
  const dayServices =
    englishDay && restaurantConfig.serviceSchedule ?
      restaurantConfig.serviceSchedule[englishDay] :
      null;
  if (!Array.isArray(dayServices)) return [];
  return dayServices
      .filter((s) => s && s.active === true)
      .map((s) => ({start: s.start, end: s.end, name: s.name, active: true, isException: false}));
};

/**
 * Intervalle de créneau par défaut (minutes) quand rien n'est configuré.
 * Aligné sur `DEFAULT_SLOT_INTERVAL_MINUTES` du backend (serviceParameterConstants).
 */
export const DEFAULT_SLOT_INTERVAL_MINUTES = 15;

/**
 * Intervalle de créneau (minutes) applicable à un service pour une date donnée.
 *
 * Cascade de résolution : paramètre du service (planning du jour) >
 * `globalParameters.interval` > défaut 15. Lit `serviceSchedule` brut (et non
 * `getNormalServicesForDate`, qui écarte les `parameters`) pour accéder à
 * l'intervalle propre au service. Les exceptions ne sont pas prises en compte
 * ici : l'usage visé (regroupement des walk-ins « du jour ») reste le cas
 * simple du planning normal.
 *
 * @param {Object} restaurantConfig - Configuration (`serviceSchedule`, `globalParameters`)
 * @param {Date} date - Date visée
 * @param {string} [serviceName] - Nom du service ('midi', 'soir', …)
 * @return {number} Intervalle en minutes (toujours > 0)
 */
export const resolveSlotIntervalForDate = (restaurantConfig, date, serviceName) => {
  const globalInterval = restaurantConfig?.globalParameters?.interval;
  const fallback =
    Number.isFinite(globalInterval) && globalInterval > 0 ?
      globalInterval :
      DEFAULT_SLOT_INTERVAL_MINUTES;

  if (!restaurantConfig || !date) return fallback;

  const englishDay = englishWeekdayFor(date);
  const dayServices =
    englishDay && restaurantConfig.serviceSchedule ?
      restaurantConfig.serviceSchedule[englishDay] :
      null;
  if (!Array.isArray(dayServices)) return fallback;

  const service = serviceName ?
    dayServices.find(
        (s) =>
          s &&
          typeof s.name === 'string' &&
          s.name.toLowerCase() === String(serviceName).toLowerCase(),
    ) :
    null;

  const serviceInterval = service?.parameters?.interval;
  return Number.isFinite(serviceInterval) && serviceInterval > 0 ?
    serviceInterval :
    fallback;
};

/**
 * Cœur partagé : services OUVERTS imposés par une exception applicable.
 *
 * Gère les deux modèles de donnée :
 * - PARTIEL (`servicesMode === 'partial'`) : une entrée `{name, closed:true}`
 *   ferme explicitement ce moment ; un moment ABSENT suit le planning normal
 *   (donc reste ouvert). C'est le modèle écrit par la fermeture datée par service.
 * - REPLACE (historique) : les services listés sont les SEULS ouverts ; un moment
 *   absent est fermé ce jour-là.
 *
 * Renvoie `null` lorsque l'exception n'impose rien sur les services (l'appelant
 * retombe alors sur le planning normal).
 *
 * @param {Object} exception - Exception applicable
 * @param {Array<Object>} normalDayServices - Services normaux du jour (getNormalServicesForDate)
 * @return {Array<Object>|null} Services ouverts, ou null si sans impact
 */
export const resolveExceptionOpenServices = (exception, normalDayServices) => {
  if (!exception) return null;
  if (exception.isClosed) return [];

  const list = exception.services;
  if (!Array.isArray(list) || list.length === 0) return null;

  const fromException = list
      .filter((s) => s && !s.closed)
      .map((s) => ({
        start: s.start,
        end: s.end,
        name: s.name,
        active: true,
        isException: true,
        exceptionName: exception.name,
      }));

  // Sémantique historique : les services listés sont les seuls ouverts.
  if (exception.servicesMode !== 'partial') {
    return fromException;
  }

  // Mode partiel : les moments non mentionnés suivent le planning normal.
  const named = new Set(list.map((s) => s && s.name).filter(Boolean));
  const fromSchedule = (normalDayServices || []).filter((s) => s && !named.has(s.name));
  return [...fromException, ...fromSchedule];
};

/**
 * Impact d'une exception sur UN moment (midi, soir) : dérivé de
 * `resolveExceptionOpenServices`, donc identique pour les deux modèles.
 *
 * `affected` est faux quand l'exception ne change rien pour ce moment : le
 * lecteur retombe alors sur le planning normal, comme s'il n'y avait pas
 * d'exception. C'est le cas d'un moment absent d'une exception partielle
 * (il suit sa variante normale, ouverte ou fermée).
 *
 * @param {Object} exception - Exception applicable
 * @param {string} serviceName - Moment visé ('midi', 'soir')
 * @param {Array<Object>} normalDayServices - Services normaux du jour (getNormalServicesForDate)
 * @return {{affected: boolean, isClosed: boolean, hasService: boolean, isExceptional: boolean, isImplicitClosure: boolean}}
 *   `isExceptional` : le moment est ouvert (ou modifié) PAR l'exception ;
 *   `isImplicitClosure` : fermé parce qu'absent d'une exception en mode replace.
 */
export const resolveExceptionServiceImpact = (exception, serviceName, normalDayServices) => {
  const none = {affected: false, isClosed: false, hasService: false, isExceptional: false, isImplicitClosure: false};
  if (!exception || !serviceName) return none;

  if (exception.isClosed) {
    return {affected: true, isClosed: true, hasService: false, isExceptional: false, isImplicitClosure: false};
  }

  const openServices = resolveExceptionOpenServices(exception, normalDayServices);
  if (openServices === null) {
    // Horaires généraux sans liste de services : tous les moments du jour sont modifiés.
    if (exception.openTime && exception.closeTime) {
      return {affected: true, isClosed: false, hasService: true, isExceptional: true, isImplicitClosure: false};
    }
    return none;
  }

  const entry = openServices.find((s) => s.name === serviceName);
  if (entry) {
    const isExceptional = entry.isException === true;
    return {affected: isExceptional, isClosed: false, hasService: true, isExceptional, isImplicitClosure: false};
  }

  const explicitlyClosed = (exception.services || []).some((s) => s && s.name === serviceName && s.closed);
  if (!explicitlyClosed && exception.servicesMode === 'partial') {
    // Moment ni fermé ni ouvert par l'exception, et absent du planning : fermeture ordinaire.
    return none;
  }

  return {affected: true, isClosed: true, hasService: false, isExceptional: false, isImplicitClosure: !explicitlyClosed};
};

/**
 * Services OUVERTS pour une date, exceptions résolues contre le planning normal.
 * Fonction pure (réutilisable hors React) : source unique de la disponibilité
 * de service par date, partagée par les hooks et l'onglet Exceptions.
 *
 * @param {Object} restaurantConfig - Configuration (`serviceSchedule`, `exceptions`)
 * @param {Date} date - Date visée
 * @return {Array<Object>} Services ouverts ce jour-là
 */
export const resolveOpenServicesForDate = (restaurantConfig, date) => {
  if (!restaurantConfig || !date) return [];
  const normalDayServices = getNormalServicesForDate(restaurantConfig, date);
  const applicableExceptions = getApplicableExceptions(restaurantConfig.exceptions, date);
  if (applicableExceptions.length > 0) {
    const resolved = resolveExceptionOpenServices(applicableExceptions[0], normalDayServices);
    if (resolved !== null) return resolved;
  }
  return normalDayServices;
};

/**
 * Récupère les horaires d'un service pour une date donnée
 *
 * Analyse la configuration du restaurant pour extraire les horaires
 * du service demandé pour le jour de la semaine spécifié.
 *
 * IMPORTANT : Prend en compte les exceptions (fermetures, horaires spéciaux)
 * avec la priorité suivante :
 * 1. Exceptions (fermeture ou services spécifiques)
 * 2. Horaires normaux du serviceSchedule
 *
 * @param {Object} restaurantConfig - Configuration du restaurant avec serviceSchedule et exceptions
 * @param {Date} date - Date pour laquelle récupérer les horaires
 * @param {string} serviceName - Nom du service ('midi' ou 'soir')
 * @return {Object|null} Objet avec { start: 'HH:MM', end: 'HH:MM', name, active, isException, exceptionName } ou null si non trouvé
 *
 * @example
 * const serviceHours = useServiceHours(config, new Date(), 'midi');
 * // Returns: { start: '12:00', end: '14:00', name: 'midi', active: true }
 *
 * @example
 * // Jour férié fermé
 * const serviceHours = useServiceHours(config, new Date('2024-12-25'), 'midi');
 * // Returns: null (fermé)
 *
 * @example
 * // Horaires spéciaux d'exception
 * const serviceHours = useServiceHours(config, new Date('2024-08-15'), 'midi');
 * // Returns: { start: '11:00', end: '15:00', name: 'midi', active: true, isException: true, exceptionName: 'Vacances été' }
 */
export const useServiceHours = (restaurantConfig, date, serviceName) => {
  return useMemo(() => {
    if (!restaurantConfig || !date || !serviceName) {
      return null;
    }

    // Les services ouverts du jour (exceptions résolues contre le planning normal,
    // mode partiel comme replace) sont calculés par la source unique
    // `resolveOpenServicesForDate` ; on ne renvoie que le service demandé.
    const openServices = resolveOpenServicesForDate(restaurantConfig, date);
    return openServices.find((s) => s && s.name === serviceName) || null;
  }, [restaurantConfig, date, serviceName]);
};

/**
 * Récupère tous les services disponibles pour une date donnée
 *
 * IMPORTANT : Prend en compte les exceptions
 * - Si fermé (isClosed = true) → retourne []
 * - Si services spécifiques définis dans l'exception → retourne ces services
 * - Sinon → retourne les services normaux du serviceSchedule
 *
 * @param {Object} restaurantConfig - Configuration du restaurant avec serviceSchedule et exceptions
 * @param {Date} date - Date pour laquelle récupérer les services
 * @return {Array<Object>} Liste des services actifs pour ce jour
 *
 * @example
 * const availableServices = useAvailableServicesForDate(config, new Date());
 * // Returns: [{ start: '12:00', end: '14:00', name: 'midi', active: true }, ...]
 *
 * @example
 * // Jour férié fermé
 * const availableServices = useAvailableServicesForDate(config, new Date('2024-12-25'));
 * // Returns: [] (fermé)
 *
 * @example
 * // Horaires spéciaux avec un seul service
 * const availableServices = useAvailableServicesForDate(config, new Date('2024-08-15'));
 * // Returns: [{ start: '11:00', end: '16:00', name: 'midi', active: true, isException: true }]
 */
export const useAvailableServicesForDate = (restaurantConfig, date) => {
  return useMemo(
      () => resolveOpenServicesForDate(restaurantConfig, date),
      [restaurantConfig, date],
  );
};

/**
 * Génère les créneaux horaires disponibles pour un service
 *
 * Crée une liste de créneaux d'arrivée, de la première à la dernière arrivée
 * incluses, au pas donné. La lecture des horaires (créneau unique, 24 h, service
 * de nuit) est déléguée à la source unique `serviceArrivalWindow`.
 *
 * @param {string} startTime - Première arrivée au format 'HH:MM'
 * @param {string} endTime - Dernière arrivée au format 'HH:MM'
 * @param {number} intervalMinutes - Intervalle entre les créneaux (défaut: 5)
 * @return {Array<Object>} Liste d'objets { hours, minutes, display } représentant les créneaux
 *
 * @example
 * const slots = generateTimeSlots('12:00', '14:00', 15);
 * // Returns: [{ hours: 12, minutes: 0, display: '12:00' }, { hours: 12, minutes: 15, display: '12:15' }, ...]
 * generateTimeSlots('12:15', '12:15', 15);
 * // Returns: [{ hours: 12, minutes: 15, display: '12:15' }]
 */
export const generateTimeSlots = (startTime, endTime, intervalMinutes = 5) => {
  const arrival = serviceArrivalWindow({start: startTime, end: endTime});
  const step = Number(intervalMinutes);
  if (!arrival || !(step > 0)) {
    return [];
  }

  const slots = [];
  for (let minute = arrival.from; minute <= arrival.to; minute += step) {
    const minuteOfDay = minute % MINUTES_PER_DAY;
    const hours = Math.floor(minuteOfDay / 60);
    const minutes = minuteOfDay % 60;
    slots.push({
      hours,
      minutes,
      display: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`,
    });
  }
  return slots;
};

/**
 * Vérifie si une heure donnée est dans la plage horaire d'un service
 *
 * @param {number} hours - Heure (0-23)
 * @param {number} minutes - Minutes (0-59)
 * @param {Object} serviceHours - Objet avec { start: 'HH:MM', end: 'HH:MM' }
 * @return {boolean} true si l'heure est dans la plage, false sinon
 *
 * @example
 * isTimeInServiceRange(12, 30, { start: '12:00', end: '14:00' }); // true
 * isTimeInServiceRange(19, 0, { start: '12:00', end: '14:00' }); // false
 */
export const isTimeInServiceRange = (hours, minutes, serviceHours) => {
  const arrival = serviceArrivalWindow(serviceHours || {});
  if (!arrival) {
    return false;
  }

  // Une heure après minuit appartient au service de nuit de la veille : on la
  // teste aussi décalée d'un jour sur l'axe du service.
  const minuteOfDay = hours * 60 + minutes;
  return [minuteOfDay, minuteOfDay + MINUTES_PER_DAY].some((m) => m >= arrival.from && m <= arrival.to);
};
